/**
 * Regeln für den Hintergrund des Finisher Frames: welche Angaben wo stehen dürfen.
 *
 * Maße in Millimetern auf dem 250 × 250 mm großen Hintergrund, abgeleitet aus
 * `shop/tools/event_background.py`. Das Backend prüft dieselben Grenzen noch einmal in
 * `ShopOrderRules.kt`; ändert sich hier etwas, muss es dort mitgehen.
 */

export const FRAME_SIZE_MM = 250;
/** Fenster, in dem der Geländeblock liegt: links, oben, rechts, unten. */
export const FRAME_BLOCK_WINDOW = [45, 38.93, 205.02, 199.5] as const;
export const MAX_FRAME_ENTRIES = 4;
/** Der Kunde will kein Jahr auf dem Rahmen. Steht auch in `ShopOrderRules.kt`. */
export const FRAME_NO_YEAR = 0;
const FIRST_FRAME_YEAR = 1950;

export function frameYearError(year: number, now = new Date()): string | null {
  return year === FRAME_NO_YEAR || (year >= FIRST_FRAME_YEAR && year <= now.getFullYear() + 1)
    ? null : 'Bitte gib ein gültiges Jahr an, zum Beispiel 2019.';
}
export const MAX_FRAME_LABEL_LENGTH = 16;

export type FrameSlotId =
  | 'name'
  | 'unten-links' | 'unten-mitte' | 'unten-rechts';

export interface FrameSlot {
  id: FrameSlotId;
  /** So heißt die Position in der Auswahl. */
  title: string;
  x: number;
  y: number;
  /** Verfügbare Breite; längere Werte werden kleiner gesetzt. */
  width: number;
  maxValueLength: number;
}

const CENTER = (FRAME_BLOCK_WINDOW[0] + FRAME_BLOCK_WINDOW[2]) / 2;
const BELOW = FRAME_BLOCK_WINDOW[3];
const ROW_STEP = 46;

export const FRAME_SLOTS: FrameSlot[] = [
  { id: 'name', title: 'Unter dem Block, groß', x: CENTER, y: BELOW + 13, width: 150, maxValueLength: 32 },
  { id: 'unten-links', title: 'Unten links', x: CENTER - ROW_STEP, y: BELOW + 23, width: 44, maxValueLength: 16 },
  { id: 'unten-mitte', title: 'Unten Mitte', x: CENTER, y: BELOW + 23, width: 44, maxValueLength: 16 },
  { id: 'unten-rechts', title: 'Unten rechts', x: CENTER + ROW_STEP, y: BELOW + 23, width: 44, maxValueLength: 16 },
];

export const FRAME_LABEL_SUGGESTIONS = [
  'Name', 'Zeit', 'Distanz', 'Höhenmeter', 'Platzierung', 'Altersklasse', 'Startnummer', 'Schnitt', 'Datum', 'Verein',
];

export interface FrameEntry {
  label: string;
  value: string;
  slot: FrameSlotId;
}

const NAME_FONT_MM = 5.2;
const VALUE_FONT_MM = 6.2;
/** Mittlere Zeichenbreite im Verhältnis zur Schriftgröße, eher großzügig geschätzt. */
const CHAR_WIDTH = 0.6;

export function frameSlot(id: FrameSlotId): FrameSlot {
  return FRAME_SLOTS.find(slot => slot.id === id) as FrameSlot;
}

export function isFrameSlot(id: string): id is FrameSlotId {
  return FRAME_SLOTS.some(slot => slot.id === id);
}

/**
 * Wo die Positionen bei diesen Angaben stehen. Die Zeile unter dem Namen bleibt mittig, egal wie
 * viele Angaben sie trägt. Der nächste freie Platz steht rechts daneben und schiebt nichts.
 */
export function frameLayout(entries: FrameEntry[]): FrameSlot[] {
  const row = FRAME_SLOTS.filter(slot => slot.id !== 'name');
  const placed = row.filter(slot => entries.some(entry => entry.slot === slot.id));
  const first = CENTER - ((placed.length - 1) / 2) * ROW_STEP;
  const free = row.find(slot => !placed.includes(slot));
  return [
    FRAME_SLOTS[0],
    ...placed.map((slot, i) => ({ ...slot, x: first + i * ROW_STEP })),
    ...(free ? [{ ...free, x: placed.length ? first + placed.length * ROW_STEP : CENTER }] : []),
  ];
}

export function frameFontSize(slot: FrameSlot, value: string): number {
  const base = slot.id === 'name' ? NAME_FONT_MM : VALUE_FONT_MM;
  return Math.min(base, slot.width / (CHAR_WIDTH * Math.max(value.length, 1)));
}

export function firstFreeFrameSlot(entries: FrameEntry[]): FrameSlotId | null {
  const taken = new Set(entries.map(entry => entry.slot));
  return FRAME_SLOTS.find(slot => !taken.has(slot.id))?.id ?? null;
}

/** Eine belegte Position wird getauscht, damit nie zwei Angaben übereinander stehen. */
export function moveFrameEntry(entries: FrameEntry[], index: number, slot: FrameSlotId): FrameEntry[] {
  const from = entries[index].slot;
  return entries.map((entry, i) => {
    if (i === index) return { ...entry, slot };
    return entry.slot === slot ? { ...entry, slot: from } : entry;
  });
}

/**
 * Beim Wechsel des Events bleibt, was jemand eingetragen hat. Nur Werte, die noch unser Vorschlag
 * für das alte Event waren, wechseln auf den Vorschlag des neuen.
 */
export function carryFrameEntries(entries: FrameEntry[], from: FrameEntry[], to: FrameEntry[]): FrameEntry[] {
  return entries.map(entry => {
    const same = (other: FrameEntry) => other.slot === entry.slot && other.label === entry.label;
    const next = to.find(same);
    return next && from.find(same)?.value === entry.value ? next : entry;
  });
}

/** Liefert je Angabe den Fehler oder null; leer heißt: bestellbar. */
export function frameEntryErrors(entries: FrameEntry[]): (string | null)[] {
  return entries.map(entry => {
    const label = entry.label.trim();
    const value = entry.value.trim();
    const slot = frameSlot(entry.slot);
    if (!label) return 'Bitte gib eine Bezeichnung an.';
    if (!value) return `Bitte trag bei „${label}“ einen Wert ein.`;
    if (label.length > MAX_FRAME_LABEL_LENGTH) return `Die Bezeichnung darf höchstens ${MAX_FRAME_LABEL_LENGTH} Zeichen haben.`;
    if (value.length > slot.maxValueLength) return `An dieser Position passen höchstens ${slot.maxValueLength} Zeichen.`;
    return null;
  });
}
